import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import "../../App.css";
import { TransitionUp } from "../../common/TransitionComponents";
import CatalogsIconButton from "../../components/customForms/CatalogsIconButton";
import ItemDashboard from "../../components/ItemDashboard";
import ItemDashboardFavorites from "../../components/ItemDashboardFavorites";
import { favorites } from "../../constants/models";
import { app } from "../../services";
import {
  buildViewerPath,
  resolveFavoriteViewerId,
  resolveViewerDeviceSectionId,
} from "../../utils/deviceApplications";

function CEMEDIDashboardView() {
  const history = useHistory();
  const token = useSelector((state) => state.session.token);
  const user = useSelector((state) => state.session.globalUser);
  const institution = useSelector((state) => state.session.institution);
  const activeApplication =
    useSelector((state) => state.session.activeApplication) || "tgone";
  const institutionId = institution?.id || institution?.id_institucion;
  const [selected, setSelected] = useState(null);
  const [sectionsDevices, setSectionsDevices] = useState([]);
  const [sectionsDevicesAlarms, setSectionsDevicesAlarms] = useState([]);
  const favoritesServices = app.service("favorites");
  const alarmsServices = app.service("devices-alarms");
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up("md"), {
    defaultMatches: true,
  });
  const favoritesModel = useMemo(
    () => ({
      ...favorites,
      columns: (favorites.columns || []).map((column) => {
        if (column.id !== "id_sectionDevice") return column;
        const applicationField =
          activeApplication === "cemedi" ? "bl_cemedi" : "bl_tgone";
        return {
          ...column,
          relation: {
            ...column.relation,
            defaultSystem: [
              ...(column.relation?.defaultSystem || []).filter(
                (item) => !["bl_tgone", "bl_cemedi"].includes(item.vc_field),
              ),
              { vc_field: applicationField, type: "static", value: true },
            ],
          },
        };
      }),
    }),
    [activeApplication],
  );

  async function getData() {
    if (!token || !institutionId || !user?.id) return;
    const response = await favoritesServices.find({
      query: {
        application: activeApplication,
        $and: [{ id_institucion: institutionId }, { id_usuario: user.id }],
      },
    });
    setSectionsDevices(response.data || []);
  }

  async function getAlarmsData() {
    if (!token || !sectionsDevices.length) return;
    const response = await alarmsServices.find({
      query: {
        $or: sectionsDevices.map((item) => ({
          id_dispositivoSeccion: item?.id_sectionDevice,
        })),
      },
    });
    setSectionsDevicesAlarms(response.data || []);
  }

  useEffect(() => {
    if (!token) return undefined;
    const handleRefresh = () => getData();
    favoritesServices.removeAllListeners();
    favoritesServices.on("created", handleRefresh);
    favoritesServices.on("remove", handleRefresh);
    return () => {
      favoritesServices.off("created", handleRefresh);
      favoritesServices.off("remove", handleRefresh);
    };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getData();
  }, [token, institutionId, user?.id, activeApplication]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (sectionsDevices.length) {
      setSectionsDevicesAlarms([]);
      getAlarmsData();
    } else {
      setSectionsDevicesAlarms([]);
    }
  }, [sectionsDevices]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDelete(id) {
    setSectionsDevices(sectionsDevices.filter((item) => item.id !== id));
    await favoritesServices.remove(id);
    setSelected(null);
  }

  const selectedDeviceSectionId = selected
    ? resolveViewerDeviceSectionId(selected)
    : null;
  const selectedViewerId = resolveFavoriteViewerId(selected);
  const selectedDevice = useMemo(
    () =>
      selected?.dispositivo
        ? { ...selected.dispositivo, id: selectedDeviceSectionId }
        : null,
    [selected, selectedDeviceSectionId],
  );

  if (!token || !institutionId) {
    return (
      <Grid
        direction="column"
        container
        display="flex"
        style={{
          height: "100%",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Helmet>
          <title>TGOne</title>
          <meta name="description" content="TGOne" />
        </Helmet>
        <Box color="text.secondary">
          {"Inicia sesión y selecciona una institución para ver el dashboard"}
        </Box>
      </Grid>
    );
  }

  return (
    <Grid
      direction="column"
      container
      display="flex"
      style={{ height: "100%" }}
    >
      <Helmet>
        <title>TGOne</title>
        <meta name="description" content="TGOne" />
      </Helmet>
      <Grid item xs="auto" style={{ width: "100%" }}>
        <Grid
          item
          xs={12}
          container
          width="100%"
          style={{
            display: "flex",
            justifyContent: "center",
            margin: "0px 0px",
            borderBottom: "0.5px solit gray",
          }}
        >
          <Grid item xs="auto" container flexDirection="row">
            <Typography
              paddingTop={0.5}
              paddingLeft={2}
              width="100%"
              fontSize={20}
              fontWeight="700"
            >
              {"Favoritos"}
            </Typography>
          </Grid>
          <Grid item xs="auto" justifyItems="center" p="0px 0px">
            <CatalogsIconButton
              backgroundColor="transparent"
              model={favoritesModel}
              icon="assets/icons/add.svg"
              size={20}
            />
          </Grid>
          <Grid item xs justifyItems="center" p="3px 8px" />
        </Grid>
      </Grid>
      <Grid
        item
        container
        xs
        alignContent="flex-start"
        style={{ width: "100%", overflow: "scroll", padding: 0 }}
      >
        {sectionsDevices.length === 0 && (
          <Button fullWidth>{"No hay favoritos registrados"}</Button>
        )}
        {sectionsDevices.map((sectionDevice, index) => {
          const favoriteKey =
            sectionDevice.id_sectionDevice || sectionDevice.id || index;
          const favoriteDevice = sectionDevice.dispositivo
            ? {
                ...sectionDevice.dispositivo,
                id:
                  sectionDevice.id_sectionDevice ||
                  sectionDevice.dispositivo.id,
              }
            : null;
          return (
            <Grid
              item
              xs={
                isMd || window.screen.availWidth > window.screen.availHeight
                  ? 2
                  : window.screen.availWidth < 300
                    ? 12
                    : 6
              }
              key={favoriteKey}
              position="relative"
            >
              <Box onClick={() => setSelected(sectionDevice)} p={1}>
                <Box flexDirection="row" display="flex" width="100%">
                  <Tooltip
                    title={sectionDevice?.dispositivo?.vc_nombre || ""}
                    placement="top"
                  >
                    <Typography
                      p={1}
                      paddingTop={0}
                      width="100%"
                      fontWeight="500"
                      fontSize={14}
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {sectionDevice?.dispositivo?.vc_nombre}
                    </Typography>
                  </Tooltip>
                </Box>
                <ItemDashboardFavorites
                  stopData={!!selected}
                  key={favoriteKey}
                  selected={favoriteDevice}
                  alarms={sectionsDevicesAlarms?.filter(
                    (item) =>
                      item.id_dispositivoSeccion ===
                      sectionDevice.id_sectionDevice,
                  )}
                  lite
                />
              </Box>
            </Grid>
          );
        })}
      </Grid>
      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        TransitionComponent={TransitionUp}
        fullScreen={!isMd}
        fullWidth
        maxWidth="md"
        PaperProps={{
          style: {
            borderRadius: 10,
            display: "flex",
            flexDirection: "column",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>{selectedDevice?.vc_nombre}</DialogTitle>
        <DialogContent sx={{ flex: 1, overflowY: "auto" }}>
          <Box width="100%" maxWidth={860}>
            <ItemDashboard
              key={selectedDeviceSectionId || "selected"}
              selected={selectedDevice}
              alarms={sectionsDevicesAlarms?.filter(
                (item) =>
                  item.id_dispositivoSeccion === selected?.id_sectionDevice,
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid rgba(255,255,255,0.12)" }}>
          <Button onClick={() => handleDelete(selected.id)}>
            {"Eliminar"}
          </Button>
          <Button onClick={() => setSelected(null)}>{"Cancelar"}</Button>
          <Button
            disabled={!selectedViewerId}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!selectedViewerId) return;
              setSelected(null);
              history.push(buildViewerPath(selectedViewerId));
            }}
          >
            {"Ver"}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default CEMEDIDashboardView;
